import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'
import { Dropdown, MenuProps, notification, Typography } from 'antd'
import dayjs from 'dayjs'
import Api from 'services/api'
import { BuildingFile } from 'types/building'

import EllipsisIcon from 'components/icons/EllipsisIcon'

import ModalEditFileName from '../../../panoramas/components/ModalEditFileName'

type Props = {
  item: BuildingFile
  handleToggleDeleteFile: (file: BuildingFile | undefined) => void
  handleSetRevalidateByCategory: () => void
  onOpen?: () => void
  existingNames?: string[]
}

export default function BuildingFileDetail({
  item,
  handleToggleDeleteFile,
  handleSetRevalidateByCategory,
  onOpen,
  existingNames
}: Props) {
  const { t } = useTranslation()
  const [isRenaming, setIsRenaming] = useState(false)

  const handleDownloadFile = async () => {
    try {
      notification.info({
        message: t('building.downloading'),
        key: 'downloading',
        icon: <LoadingOutlined className="theme-text-primary" />
      })
      // Fetch fresh file detail from API to avoid expired S3 URLs
      const fileDetail = await Api.get<BuildingFile>(
        `/v1/buildings/files/${item.id}`
      )
      const downloadPath = fileDetail?.s3Path || item.s3Path

      const response = await fetch(downloadPath)
      if (!response.ok) throw new Error('Failed to fetch file content')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = item.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      notification.success({
        message: t('building.downloadSuccess'),
        key: 'downloading'
      })
    } catch (error) {
      console.error('Download error:', error)
      notification.error({
        message: t('common.error'),
        key: 'downloading'
      })
    }
  }

  const handleRename = async (newName: string) => {
    try {
      await Api.patch(`/v1/buildings/files/${item.id}`, { name: newName })
      notification.success({
        message: t('building.saved')
      })
      handleSetRevalidateByCategory()
      setIsRenaming(false)
    } catch (error) {
      notification.error({
        message: t('building.failed')
      })
    }
  }

  const dropdownItems: MenuProps['items'] = [
    { key: 'rename', label: '名前を変更' },
    { key: 'delete', label: '削除' }
  ]

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation()
    switch (key) {
      case 'open':
        onOpen?.()
        break
      case 'rename':
        setIsRenaming(true)
        break
      case 'download':
        handleDownloadFile()
        break
      case 'delete':
        handleToggleDeleteFile(item)
        break
      default:
        break
    }
  }

  return (
    <>
      <div className="border-0 border-b border-solid theme-border-secondary">
        <div
          className="flex justify-between pl-[12px] pr-[10px] py-[6px] min-h-[50px] theme-bg-third cursor-pointer hover:bg-[#ffffff20] transition-colors"
          onClick={() => onOpen?.()}
        >
          <div className="flex flex-col gap-[2px] justify-center" key={item.id}>
            <Typography.Text className="text-white font-[400] text-[13px] leading-[19.5px]">
              {item?.name}
            </Typography.Text>
            <Typography className="text-white font-[400] text-[11px] leading-[16.5px]">
              {dayjs(item?.createdAt).format('YYYY/MM/DD HH:mm')}
            </Typography>
          </div>
          <div className="my-auto flex items-center justify-center">
            <Dropdown
              menu={{
                items: dropdownItems,
                onClick: handleMenuClick
              }}
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="building-file-detail-dropdown"
            >
              <div
                className="text-white flex items-center justify-center w-6 h-6 rounded-full hover:bg-[#D9D9D94A] transition-colors cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisIcon color="#FFFFFF" />
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
      {isRenaming && (
        <ModalEditFileName
          file={item}
          existingNames={existingNames}
          onCancel={() => setIsRenaming(false)}
          onSuccess={handleRename}
        />
      )}
    </>
  )
}

BuildingFileDetail.defaultProps = {
  onOpen: undefined,
  existingNames: undefined
}
